// template-preflight.ts — the client half of the launch preflight: render
// a result as one line per check + the summary, and drive the
// "launch anyway" acknowledgement the create-release modal and the fork
// dialog both need before Start (owner batch 2026-09-06).
//
// Pure decisions (preflightVm, ackVm) are exported separately from the DOM
// mount so the suites drill them without a browser (the create-release.ts
// precedent). The DOM discipline is dialog.ts's: createElement/textContent,
// no innerHTML except the icon() sprite refs the buttons already use.

import { mkFcToggle } from './form-controls.js';

export interface PreflightCheckDto {
  check: string;
  status: 'PASS' | 'WARN' | 'FAIL' | 'SKIP';
  detail: string;
  remedy?: string;
}

export interface PreflightDto {
  id?: number | null;
  summary: string;
  ok: boolean;
  warns: string[];
  fails: string[];
  checks: PreflightCheckDto[];
  counts: { checks: number; pass: number; warn: number; fail: number; skip: number };
}

export interface PreflightVm {
  /** 'blocked' (FAIL present) · 'ack' (WARN needs acknowledgement) · 'clear'. */
  verdict: 'blocked' | 'ack' | 'clear';
  summary: string;
  /** The lines to paint, non-PASS first (FAIL, WARN), then PASS, SKIP last. */
  lines: { status: PreflightCheckDto['status']; text: string; remedy: string | null }[];
  /** The WARN check names the acknowledgement must carry. */
  ackNames: string[];
  submitLabel: string;
}

const ORDER: Record<PreflightCheckDto['status'], number> = { FAIL: 0, WARN: 1, PASS: 2, SKIP: 3 };

export function preflightVm(r: PreflightDto | null, verb = 'Create'): PreflightVm {
  if (!r) {
    return { verdict: 'clear', summary: '', lines: [], ackNames: [], submitLabel: verb };
  }
  const lines = [...r.checks]
    .sort((a, b) => ORDER[a.status] - ORDER[b.status])
    .map((c) => ({ status: c.status, text: `${c.status} ${c.check} — ${c.detail}`, remedy: c.status === 'PASS' || c.status === 'SKIP' ? null : (c.remedy ?? null) }));
  if (!r.ok) return { verdict: 'blocked', summary: r.summary, lines, ackNames: [], submitLabel: `${verb} (blocked by preflight)` };
  if (r.warns.length) return { verdict: 'ack', summary: r.summary, lines, ackNames: [...r.warns], submitLabel: `${verb} anyway` };
  return { verdict: 'clear', summary: r.summary, lines, ackNames: [], submitLabel: verb };
}

/** May the form submit? FAIL never; WARN only with the box ticked; clear always. */
export function ackVm(vm: PreflightVm, acknowledged: boolean): { canSubmit: boolean; reason: string | null } {
  if (vm.verdict === 'blocked') return { canSubmit: false, reason: `Preflight FAIL — ${vm.summary}` };
  if (vm.verdict === 'ack' && !acknowledged) return { canSubmit: false, reason: `Preflight WARN — tick "launch anyway" to acknowledge: ${vm.ackNames.join(', ')}` };
  return { canSubmit: true, reason: null };
}

/** What the POST body carries for the server's gate: the acknowledged WARN
 * names (never `true` — the client names what it saw). ALWAYS present once
 * the dialog exists, `[]` when nothing was acknowledged: its presence is
 * what tells the server "this caller ran the preflight — enforce every
 * WARN", so the UI can never launch past a WARN it did not name. */
export function ackBody(vm: PreflightVm, acknowledged: boolean): { preflight_ack: string[] } {
  return { preflight_ack: vm.verdict === 'ack' && acknowledged ? vm.ackNames : [] };
}

/** The DOM seat: a status line, a collapsible check list, and the
 * acknowledgement switch (shown only on 'ack'). `onChange` fires when the
 * acknowledgement flips so the host re-judges its submit button. The switch
 * is the toggle primitive (mkFcToggle, role=switch) — the primitive-catalog
 * ruling allows a native checkbox for grid bulk-select ONLY, and
 * primitives-check refused the first cut's <input type=checkbox> by name.
 *
 * D4 (create-release-browsers.md): nothing in the form flow flips [hidden]
 * after build and the modal's box never changes size on a state change —
 * so the details row and the acknowledgement row are RESERVED (fixed
 * height) and toggle VISIBILITY through `.hc-off`; the check list opens
 * only on the operator's click (a <details> the user expands is the one
 * size change that is theirs). */
export interface PreflightMount {
  el: HTMLElement;
  /** Paint a result (or the in-flight/absent state). */
  set(r: PreflightDto | null, state?: 'running' | 'idle' | 'error', note?: string): PreflightVm;
  acknowledged(): boolean;
  vm(): PreflightVm;
}

export function mountPreflight(doc: Document, opts: { verb: string; onChange: () => void; todo?: (fail: PreflightCheckDto) => string }): PreflightMount {
  const root = doc.createElement('div');
  root.className = 'hc-preflight';
  const status = doc.createElement('p');
  status.className = 'hc-status hc-preflight-status';
  status.setAttribute('aria-live', 'polite');
  root.appendChild(status);
  // OPT-IN (owner-reported 2026-10-09): a reserved line under the status that
  // says what to DO about the first FAIL — the remedy used to live only in the
  // collapsed list, so a blocked Create read as "it immediately failed". Only
  // a host passing `todo` gets the seat; every other host is unchanged.
  const todo = opts.todo ? doc.createElement('p') : null;
  if (todo) {
    todo.className = 'hc-status hc-preflight-todo';
    root.appendChild(todo);
  }
  const details = doc.createElement('details');
  details.className = 'hc-preflight-details';
  const summary = doc.createElement('summary');
  summary.textContent = 'Preflight checks';
  details.appendChild(summary);
  const list = doc.createElement('ul');
  list.className = 'hc-preflight-list';
  details.appendChild(list);
  details.classList.add('hc-off');
  root.appendChild(details);
  const ackLabel = doc.createElement('div');
  ackLabel.className = 'hc-preflight-ack';
  const ack = mkFcToggle({
    label: 'Launch anyway',
    text: '',
    cls: 'hc-preflight-ack-tg',
    onChange: () => opts.onChange(),
  });
  ackLabel.appendChild(ack.el);
  const ackText = doc.createElement('span');
  ackText.className = 'hc-preflight-ack-text';
  ackLabel.appendChild(ackText);
  ackLabel.classList.add('hc-off');
  root.appendChild(ackLabel);
  let current: PreflightVm = preflightVm(null, opts.verb);
  return {
    el: root,
    set(r, state = 'idle', note) {
      current = preflightVm(r, opts.verb);
      ack.checked = false;
      list.textContent = '';
      const firstFail = r && state === 'idle' ? r.checks.find((c) => c.status === 'FAIL') ?? null : null;
      if (todo) todo.textContent = firstFail && opts.todo ? opts.todo(firstFail) : '';
      if (state === 'running') {
        status.textContent = 'Running the launch preflight…';
        details.classList.add('hc-off');
        ackLabel.classList.add('hc-off');
        opts.onChange();
        return current;
      }
      if (state === 'error' || !r) {
        status.textContent = note ?? (state === 'error' ? 'Preflight unavailable — the server will judge at submit.' : '');
        details.classList.add('hc-off');
        ackLabel.classList.add('hc-off');
        opts.onChange();
        return current;
      }
      status.textContent = current.summary;
      status.dataset.verdict = current.verdict;
      for (const line of current.lines) {
        const li = doc.createElement('li');
        li.className = `hc-preflight-line hc-preflight-${line.status.toLowerCase()}`;
        li.textContent = line.text;
        if (line.remedy) {
          const rem = doc.createElement('div');
          rem.className = 'hc-preflight-remedy';
          rem.textContent = `remedy: ${line.remedy}`;
          li.appendChild(rem);
        }
        list.appendChild(li);
      }
      details.classList.remove('hc-off');
      details.open = false; // the operator opens it — D4: no size change on a state flip
      if (current.verdict === 'ack') ackLabel.classList.remove('hc-off');
      else ackLabel.classList.add('hc-off');
      ackText.textContent = current.verdict === 'ack'
        ? `Launch anyway — I acknowledge: ${current.ackNames.join(', ')}`
        : '';
      // The switch's accessible name carries the named checks too — a
      // screen-reader user hears WHAT they are acknowledging, not a bare
      // "Launch anyway".
      ack.el.setAttribute('aria-label', ackText.textContent || 'Launch anyway');
      opts.onChange();
      return current;
    },
    acknowledged: () => ack.checked,
    vm: () => current,
  };
}
